/**
 * Geometry of the visualizer: a tiny pinhole camera and the "corner pin" homography that lays a flat
 * texture onto a quad in perspective.
 *
 * Why this exists: SVG has no projective transform, so a floor drawn with an SVG `<pattern>` cannot
 * recede. CSS can: `matrix3d()` accepts the full 3 × 3 homography. Every stone surface in a scene is
 * therefore an HTML plane (a `div` with the tiled texture as its background) whose local rectangle is
 * mapped onto the surface's quad on screen — tiles shrink towards the horizon, seams converge, and a
 * stone swap, scale or rotation is a plain CSS background change.
 *
 * All coordinates are "design pixels" of the 1200 × 800 frame every scene is drawn in; the stage
 * multiplies by one scale factor at render time.
 */

export type Vec2 = readonly [x: number, y: number];
export type Vec3 = readonly [x: number, y: number, z: number];
/** Four corners in screen order: top-left, top-right, bottom-right, bottom-left. */
export type Quad = readonly [Vec2, Vec2, Vec2, Vec2];

/** The frame every scene is authored in (3 : 2). */
export const DESIGN_WIDTH = 1200;
export const DESIGN_HEIGHT = 800;

/**
 * A pinhole camera looking straight down +z (no tilt, so verticals stay vertical — the "shift lens"
 * look of architectural photography). `center` is where the optical axis meets the frame; moving it
 * shifts the horizon without keystoning the walls.
 */
export interface Camera {
  readonly eye: Vec3;
  /** Focal length in design pixels. */
  readonly focal: number;
  readonly center: Vec2;
}

/** World metres → design pixels. `z` must be in front of the camera (`z > eye.z`). */
export function project(camera: Camera, point: Vec3): Vec2 {
  const depth = point[2] - camera.eye[2];
  const scale = camera.focal / depth;
  return [
    camera.center[0] + (point[0] - camera.eye[0]) * scale,
    camera.center[1] - (point[1] - camera.eye[1]) * scale,
  ];
}

/** Pixels per metre at a given depth (how large one metre looks there). */
export function pixelsPerMetre(camera: Camera, z: number): number {
  return camera.focal / (z - camera.eye[2]);
}

/** A rectangle facing the camera (a wall, a riser, a cabinet front) at depth `z`. */
export function frontalQuad(
  camera: Camera,
  x0: number,
  x1: number,
  y0: number,
  y1: number,
  z: number,
): Quad {
  return [
    project(camera, [x0, y1, z]),
    project(camera, [x1, y1, z]),
    project(camera, [x1, y0, z]),
    project(camera, [x0, y0, z]),
  ];
}

/**
 * A horizontal rectangle at height `y` (a floor, a counter top, a tread) spanning depths `z0` (near)
 * to `z1` (far). Its top edge on screen is the far edge, so texture "up" points away from the viewer.
 */
export function horizontalQuad(
  camera: Camera,
  x0: number,
  x1: number,
  z0: number,
  z1: number,
  y: number,
): Quad {
  return [
    project(camera, [x0, y, z1]),
    project(camera, [x1, y, z1]),
    project(camera, [x1, y, z0]),
    project(camera, [x0, y, z0]),
  ];
}

/** `x,y x,y …` for an SVG `points` attribute (one decimal is plenty at this scale). */
export function toPoints(points: readonly Vec2[]): string {
  return points.map(([x, y]) => `${round(x)},${round(y)}`).join(' ');
}

/** `M x y L x y … Z` for an SVG `d` attribute. */
export function toPath(points: readonly Vec2[]): string {
  return `${points.map(([x, y], index) => `${index === 0 ? 'M' : 'L'}${round(x)} ${round(y)}`).join(' ')} Z`;
}

export function round(value: number, digits = 1): number {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
}

/** Signed area of a polygon (positive when the points run clockwise on screen, y pointing down). */
export function polygonArea(points: readonly Vec2[]): number {
  let sum = 0;
  for (let index = 0; index < points.length; index += 1) {
    const a = points[index];
    const b = points[(index + 1) % points.length];
    if (a && b) sum += a[0] * b[1] - b[0] * a[1];
  }
  return sum / 2;
}

/** True when every corner turn has the same direction (no bow-ties, no dents). */
export function isConvex(points: readonly Vec2[]): boolean {
  let sign = 0;
  for (let index = 0; index < points.length; index += 1) {
    const a = points[index];
    const b = points[(index + 1) % points.length];
    const c = points[(index + 2) % points.length];
    if (!a || !b || !c) return false;
    const cross = (b[0] - a[0]) * (c[1] - b[1]) - (b[1] - a[1]) * (c[0] - b[0]);
    if (cross === 0) continue;
    const next = cross > 0 ? 1 : -1;
    if (sign !== 0 && next !== sign) return false;
    sign = next;
  }
  return sign !== 0;
}

export function quadBounds(quad: Quad): { x0: number; y0: number; x1: number; y1: number } {
  const xs = quad.map((point) => point[0]);
  const ys = quad.map((point) => point[1]);
  return {
    x0: Math.min(...xs),
    y0: Math.min(...ys),
    x1: Math.max(...xs),
    y1: Math.max(...ys),
  };
}

export function scaleQuad(quad: Quad, factor: number): Quad {
  const scaled = (point: Vec2): Vec2 => [point[0] * factor, point[1] * factor];
  return [scaled(quad[0]), scaled(quad[1]), scaled(quad[2]), scaled(quad[3])];
}

/**
 * Move every edge of a convex quad outwards by `amount` px (and re-intersect neighbouring edges).
 * Planes that abut — a stair tread and its riser, a counter and its edge — are drawn this much larger
 * so anti-aliasing never leaves a hairline of the layer below showing between them.
 */
export function dilateQuad(quad: Quad, amount: number): Quad {
  if (amount === 0) return quad;
  const centre: Vec2 = [
    (quad[0][0] + quad[1][0] + quad[2][0] + quad[3][0]) / 4,
    (quad[0][1] + quad[1][1] + quad[2][1] + quad[3][1]) / 4,
  ];
  // each edge as a line a·x + b·y = c, shifted away from the centre
  const lines = quad.map((start, index) => {
    const end = quad[(index + 1) % 4] ?? start;
    const dx = end[0] - start[0];
    const dy = end[1] - start[1];
    const length = Math.hypot(dx, dy) || 1;
    let nx = dy / length;
    let ny = -dx / length;
    if ((start[0] - centre[0]) * nx + (start[1] - centre[1]) * ny < 0) {
      nx = -nx;
      ny = -ny;
    }
    return { nx, ny, c: nx * start[0] + ny * start[1] + amount };
  });
  const corner = (prev: number, next: number, fallback: Vec2): Vec2 => {
    const a = lines[prev];
    const b = lines[next];
    if (!a || !b) return fallback;
    const det = a.nx * b.ny - a.ny * b.nx;
    if (Math.abs(det) < 1e-9) return fallback;
    return [(a.c * b.ny - a.ny * b.c) / det, (a.nx * b.c - a.c * b.nx) / det];
  };
  return [
    corner(3, 0, quad[0]),
    corner(0, 1, quad[1]),
    corner(1, 2, quad[2]),
    corner(2, 3, quad[3]),
  ];
}

/** The projective map from the unit square to a quad (Heckbert, "Fundamentals of Texture Mapping"). */
export interface Homography {
  a: number;
  b: number;
  c: number;
  d: number;
  e: number;
  f: number;
  g: number;
  h: number;
}

export function unitSquareToQuad(quad: Quad): Homography {
  const [[x0, y0], [x1, y1], [x2, y2], [x3, y3]] = quad;
  const sx = x0 - x1 + x2 - x3;
  const sy = y0 - y1 + y2 - y3;
  if (Math.abs(sx) < 1e-9 && Math.abs(sy) < 1e-9) {
    return { a: x1 - x0, b: x3 - x0, c: x0, d: y1 - y0, e: y3 - y0, f: y0, g: 0, h: 0 };
  }
  const dx1 = x1 - x2;
  const dx2 = x3 - x2;
  const dy1 = y1 - y2;
  const dy2 = y3 - y2;
  const det = dx1 * dy2 - dx2 * dy1;
  const g = (sx * dy2 - dx2 * sy) / det;
  const h = (dx1 * sy - sx * dy1) / det;
  return {
    a: x1 - x0 + g * x1,
    b: x3 - x0 + h * x3,
    c: x0,
    d: y1 - y0 + g * y1,
    e: y3 - y0 + h * y3,
    f: y0,
    g,
    h,
  };
}

/** Where the homography sends the point `(u, v)` of the unit square. */
export function applyHomography(map: Homography, u: number, v: number): Vec2 {
  const w = map.g * u + map.h * v + 1;
  return [(map.a * u + map.b * v + map.c) / w, (map.d * u + map.e * v + map.f) / w];
}

/** Twelve significant digits: the perspective terms are tiny (≈ 1e-4), so fixed decimals would lose them. */
const num = (value: number): string => String(Number(value.toPrecision(12)));

/**
 * `matrix3d(…)` that maps an element's own `width × height` box (origin top-left, so set
 * `transform-origin: 0 0`) onto `quad`. Column-major, z untouched: a pure 2-D projective warp.
 */
export function cornerPinMatrix(quad: Quad, width: number, height: number): string {
  const { a, b, c, d, e, f, g, h } = unitSquareToQuad(quad);
  return `matrix3d(${[
    a / width,
    d / width,
    0,
    g / width,
    b / height,
    e / height,
    0,
    h / height,
    0,
    0,
    1,
    0,
    c,
    f,
    0,
    1,
  ]
    .map(num)
    .join(',')})`;
}

/**
 * Local pixels per world metre for a plane, so its texture is never magnified on screen: the largest
 * screen length of a metre anywhere on the quad, capped so a very deep floor does not become a giant
 * layer.
 */
export function planeDensity(
  quad: Quad,
  size: readonly [width: number, height: number],
  cap = 420,
): number {
  const edge = (from: Vec2, to: Vec2): number => Math.hypot(to[0] - from[0], to[1] - from[1]);
  const [tl, tr, br, bl] = quad;
  const perMetre = Math.max(
    edge(tl, tr) / size[0],
    edge(bl, br) / size[0],
    edge(tl, bl) / size[1],
    edge(tr, br) / size[1],
  );
  return Math.min(cap, Math.max(40, perMetre));
}

/** Mix two `#rrggbb` colours (`t` = 0 → `a`, 1 → `b`). */
export function mixHex(a: string, b: string, t: number): string {
  const parse = (hex: string): [number, number, number] => {
    const value = Number.parseInt(hex.slice(1), 16);
    return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
  };
  const [ar, ag, ab] = parse(a);
  const [br, bg, bb] = parse(b);
  const channel = (from: number, to: number): string =>
    Math.round(from + (to - from) * t)
      .toString(16)
      .padStart(2, '0');
  return `#${channel(ar, br)}${channel(ag, bg)}${channel(ab, bb)}`;
}
